import React, { useState, useMemo, useCallback } from "react";
import { Box, Tooltip, Dialog, CircularProgress, Typography, useTheme } from "@mui/material";
import {
    GridView as ProductsIcon,
    Link as IntegrationsIcon,
    Folder as FilesIcon,
    ViewColumn as SubscriptionIcon
} from "@mui/icons-material";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import { UserButton, useAuth } from "@clerk/clerk-react";
import { openBillingPortal } from "@/api/billing";
import { BackgroundActivityNavButton } from "./backgroundActivity";

// Intelligenic brand logo (Figma import)
import NewIntelligencLogo from "../imports/250418IntIcon-13029-85";

// ── Brand constants (from design handoff) ───────────────────────────────────
const RAIL_GRADIENT =
    "linear-gradient(180deg, #081430 0%, #0E2C75 38%, #1B53EE 75%, #102B72 100%)";

const WHITE = "#fff";
const WHITE_72 = "rgba(255,255,255,0.72)";
const WHITE_55 = "rgba(255,255,255,0.55)";
const WHITE_ACTIVE_BG = "rgba(255,255,255,0.16)";
const WHITE_HOVER_BG = "rgba(255,255,255,0.09)";
const WHITE_DIVIDER = "rgba(255,255,255,0.14)";

const COLLAPSED_W = 56;
const EXPANDED_W = 224;
const EXPANDED_STORAGE_KEY = "intelligenic-nav-expanded";

interface NavigationProps {
    currentView: string;
    onViewChange: (view: string) => void;
    onJiraSyncOpen?: (context: "features" | "initiatives" | "settings") => void;
    hasProductStudio?: boolean;
    canViewContextMesh?: boolean;
    canManageFiles?: boolean;
    canManageIntegrations?: boolean;
}

type RailItemId = "products" | "context-mesh-console" | "integrations" | "files";

interface RailItem {
    id: RailItemId;
    label: string;
    sub: string;
    Icon: typeof ProductsIcon;
    /** Views that should mark this item as active. */
    activeViews: string[];
    /** View to navigate to on select. */
    targetView: string;
    bridgeId: string;
}

const RAIL_ITEMS: RailItem[] = [
    {
        id: "products",
        label: "Product Hub",
        sub: "Your product workspaces",
        Icon: ProductsIcon,
        activeViews: ["projects", "initiatives", "features", "editor", "mesh-strength"],
        targetView: "projects",
        bridgeId: "nav.projects"
    },
    {
        id: "integrations",
        label: "Integrations",
        sub: "Jira · Git · Google",
        Icon: IntegrationsIcon,
        activeViews: ["integrations"],
        targetView: "integrations",
        bridgeId: "nav.integrations"
    },
    {
        id: "files",
        label: "Files",
        sub: "Artifact files in the Context Mesh",
        Icon: FilesIcon,
        activeViews: ["uploads"],
        targetView: "uploads",
        bridgeId: "nav.uploads"
    }
];

const CONTEXT_MESH_ITEM: RailItem = {
    id: "context-mesh-console",
    label: "Context Mesh Hub",
    sub: "Standalone Context Meshes",
    Icon: HubOutlinedIcon,
    activeViews: ["context-mesh-console", "new-context-mesh"],
    targetView: "context-mesh-console",
    bridgeId: "nav.context-mesh-console"
};

export function getNavigationItems(
    hasProductStudio: boolean,
    canManageFiles: boolean,
    canManageIntegrations: boolean,
    canViewContextMesh = true
): RailItem[] {
    if (hasProductStudio) {
        return canViewContextMesh
            ? [RAIL_ITEMS[0], CONTEXT_MESH_ITEM, ...RAIL_ITEMS.slice(1)]
            : RAIL_ITEMS;
    }

    const items: RailItem[] = canViewContextMesh ? [CONTEXT_MESH_ITEM] : [];
    if (canManageIntegrations) items.push(RAIL_ITEMS[1]);
    if (canManageFiles) items.push(RAIL_ITEMS[2]);
    return items;
}

// ── Reusable tooltip props (dark MUI tooltip on right) ─────────────────────
const tooltipSlotProps = {
    tooltip: {
        sx: {
            bgcolor: "common.black",
            color: "common.white",
            fontSize: "0.75rem",
            fontWeight: 500,
            letterSpacing: "0.025em",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
            borderRadius: 1
        }
    }
} as const;

// ── One nav row (collapsed icon-only or expanded labeled) ───────────────────
interface RowProps {
    item: RailItem;
    expanded: boolean;
    active: boolean;
    onClick: () => void;
}

function RailRow({ item, expanded, active, onClick }: RowProps) {
    const { Icon } = item;
    const hoverOn = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (!active) e.currentTarget.style.background = WHITE_HOVER_BG;
    };
    const hoverOff = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (!active) e.currentTarget.style.background = "transparent";
    };

    if (expanded) {
        return (
            <button
                type="button"
                onClick={onClick}
                aria-current={active ? "page" : undefined}
                data-ui-bridge={item.bridgeId}
                data-ui-label={item.label}
                data-ui-role="button"
                data-ui-group="navigation"
                data-ui-description={`Navigate to ${item.label} page`}
                onMouseEnter={hoverOn}
                onMouseLeave={hoverOff}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 11,
                    width: "100%",
                    padding: "9px 10px",
                    border: "none",
                    borderRadius: 9,
                    cursor: "pointer",
                    background: active ? WHITE_ACTIVE_BG : "transparent",
                    textAlign: "left",
                    fontFamily: "inherit",
                    transition: "background .12s"
                }}
            >
                <span style={{ color: active ? WHITE : WHITE_72, display: "inline-flex", flex: "0 0 auto" }}>
                    <Icon sx={{ fontSize: 18 }} />
                </span>
                <span style={{ minWidth: 0, flex: 1 }}>
                    <span
                        style={{
                            display: "block",
                            fontSize: 13.5,
                            fontWeight: active ? 600 : 500,
                            color: WHITE,
                            whiteSpace: "nowrap"
                        }}
                    >
                        {item.label}
                    </span>
                    <span
                        style={{
                            display: "block",
                            fontSize: 11,
                            color: WHITE_55,
                            whiteSpace: "normal",
                            lineHeight: 1.25
                        }}
                    >
                        {item.sub}
                    </span>
                </span>
            </button>
        );
    }

    return (
        <Tooltip title={item.label} placement="right" enterDelay={300} slotProps={tooltipSlotProps}>
            <button
                type="button"
                onClick={onClick}
                aria-label={item.label}
                aria-current={active ? "page" : undefined}
                data-ui-bridge={item.bridgeId}
                data-ui-label={item.label}
                data-ui-role="button"
                data-ui-group="navigation"
                data-ui-description={`Navigate to ${item.label} page`}
                onMouseEnter={hoverOn}
                onMouseLeave={hoverOff}
                style={{
                    width: 38,
                    height: 38,
                    border: "none",
                    borderRadius: 10,
                    cursor: "pointer",
                    background: active ? WHITE_ACTIVE_BG : "transparent",
                    color: active ? WHITE : WHITE_72,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "inherit",
                    transition: "background .12s, color .12s"
                }}
            >
                <Icon sx={{ fontSize: 19 }} />
            </button>
        </Tooltip>
    );
}

// ── Main component ─────────────────────────────────────────────────────────
export function Navigation({
    currentView,
    onViewChange,
    hasProductStudio = true,
    canViewContextMesh = true,
    canManageFiles = false,
    canManageIntegrations = false
}: NavigationProps) {
    const theme = useTheme();
    const { getToken } = useAuth();
    const [isPortalLoading, setIsPortalLoading] = useState(false);
    const railItems = useMemo<RailItem[]>(
        () => getNavigationItems(hasProductStudio, canManageFiles, canManageIntegrations, canViewContextMesh),
        [hasProductStudio, canManageFiles, canManageIntegrations, canViewContextMesh]
    );

    const [expanded, setExpanded] = useState<boolean>(() => {
        if (typeof window === "undefined") return false;
        try {
            return window.localStorage.getItem(EXPANDED_STORAGE_KEY) === "true";
        } catch {
            return false;
        }
    });

    const reduceMotion = useMemo(
        () =>
            typeof window !== "undefined" &&
            !!window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches,
        []
    );

    const toggleExpanded = useCallback(() => {
        setExpanded(prev => {
            const next = !prev;
            try {
                window.localStorage.setItem(EXPANDED_STORAGE_KEY, String(next));
            } catch {
                /* ignore storage errors */
            }
            return next;
        });
    }, []);

    const handleSubscriptionClick = () => {
        setIsPortalLoading(true);
        getToken()
            .then(token => {
                if (!token) {
                    setIsPortalLoading(false);
                    return;
                }
                return openBillingPortal(token, window.location.origin);
            })
            .catch(error => {
                console.error("Failed to open billing portal:", error);
                setIsPortalLoading(false);
                alert("Failed to open subscription portal. Please try again.");
            });
    };

    const W = expanded ? EXPANDED_W : COLLAPSED_W;

    const hoverBg = (e: React.MouseEvent<HTMLElement>, on: boolean) => {
        e.currentTarget.style.background = on ? WHITE_HOVER_BG : "transparent";
    };

    return (
        <Box
            component="nav"
            aria-label="Primary"
            sx={{
                width: W,
                minWidth: W,
                flex: "0 0 auto",
                height: "100vh",
                background: RAIL_GRADIENT,
                borderRight: "1px solid rgba(8,20,48,0.25)",
                display: "flex",
                flexDirection: "column",
                fontFamily: "var(--font-sans, Inter, Roboto, sans-serif)",
                transition: reduceMotion
                    ? "none"
                    : "width .22s cubic-bezier(.4,0,.2,1), min-width .22s cubic-bezier(.4,0,.2,1)",
                overflow: "hidden",
                zIndex: theme.zIndex.drawer
            }}
        >
            {/* Header — Intelligenic logo doubles as expand/collapse toggle */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                    justifyContent: expanded ? "flex-start" : "center",
                    padding: expanded ? "12px 0 10px 10px" : "12px 0 10px",
                    color: WHITE
                }}
            >
                <Tooltip
                    title={expanded ? "Collapse" : "Expand"}
                    placement="right"
                    enterDelay={300}
                    slotProps={tooltipSlotProps}
                >
                    <button
                        type="button"
                        onClick={toggleExpanded}
                        aria-label={expanded ? "Collapse navigation" : "Expand navigation"}
                        aria-expanded={expanded}
                        data-ui-bridge="nav.toggle"
                        data-ui-label="Toggle navigation"
                        data-ui-role="button"
                        data-ui-group="navigation"
                        data-ui-description="Expand or collapse the navigation rail"
                        onMouseEnter={e => hoverBg(e, true)}
                        onMouseLeave={e => hoverBg(e, false)}
                        style={{
                            width: 38,
                            height: 38,
                            border: "none",
                            borderRadius: 10,
                            cursor: "pointer",
                            background: "transparent",
                            color: WHITE,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 4,
                            fontFamily: "inherit",
                            transition: "background .12s",
                            flex: "0 0 auto"
                        }}
                    >
                        <Box sx={{ width: 26, height: 26, color: WHITE, "& *": { "--fill-0": "currentColor !important" } }}>
                            <NewIntelligencLogo />
                        </Box>
                    </button>
                </Tooltip>
                {expanded && (
                    <span
                        style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: WHITE,
                            letterSpacing: "-0.01em",
                            whiteSpace: "nowrap"
                        }}
                    >
                        Intelligenic
                    </span>
                )}
            </Box>

            <Box sx={{ height: "1px", background: WHITE_DIVIDER, margin: "0 12px 8px" }} />

            {/* Destinations */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: expanded ? "2px" : "4px",
                    padding: expanded ? "0 10px" : 0,
                    alignItems: expanded ? "stretch" : "center"
                }}
            >
                {railItems.map(item => {
                    const active = item.activeViews.includes(currentView);
                    return (
                        <RailRow
                            key={item.id}
                            item={item}
                            expanded={expanded}
                            active={active}
                            onClick={() => onViewChange(item.targetView)}
                        />
                    );
                })}
            </Box>

            <Box sx={{ flex: 1 }} />

            {/* Cross-product processing activity */}
            <BackgroundActivityNavButton expanded={expanded} />

            {/* Profile dock — Clerk UserButton */}
            <Box
                sx={{
                    padding: expanded ? "10px 10px 12px" : "10px 0 12px",
                    borderTop: `1px solid ${WHITE_DIVIDER}`,
                    display: "flex",
                    justifyContent: expanded ? "flex-start" : "center",
                    alignItems: "center"
                }}
                data-ui-bridge="nav.profile"
                data-ui-label="Profile Menu"
                data-ui-role="button"
                data-ui-group="navigation"
                data-ui-description="Open user profile and account settings menu"
            >
                <UserButton
                    afterSignOutUrl="/splash"
                    showName={expanded}
                    appearance={{
                        elements: {
                            rootBox: {
                                width: expanded ? "100%" : "auto"
                            },
                            userButtonBox: {
                                flexDirection: "row",
                                gap: "10px",
                                width: expanded ? "100%" : "auto",
                                justifyContent: expanded ? "flex-start" : "center"
                            },
                            userButtonOuterIdentifier: {
                                color: WHITE,
                                fontSize: "12.5px",
                                fontWeight: 600,
                                maxWidth: "140px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                            },
                            avatarBox: {
                                width: expanded ? 32 : 32,
                                height: expanded ? 32 : 32,
                                boxShadow: "0 0 0 2px rgba(255,255,255,0.22)"
                            }
                        }
                    }}
                >
                    <UserButton.MenuItems>
                        <UserButton.Action
                            label="Subscription"
                            labelIcon={<SubscriptionIcon />}
                            onClick={handleSubscriptionClick}
                        />
                    </UserButton.MenuItems>
                </UserButton>
            </Box>

            {/* Subscription Portal Loading Modal */}
            <Dialog
                open={isPortalLoading}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        px: 6,
                        py: 5,
                        minWidth: 320,
                        textAlign: "center"
                    }
                }}
            >
                <Typography variant="h6" sx={{ color: "primary.main", fontWeight: 600, mb: 3 }}>
                    Connecting to Your Subscription Portal
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                    <CircularProgress size={48} thickness={4} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    Please wait...
                </Typography>
            </Dialog>
        </Box>
    );
}
